import styled from "styled-components";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { red } from "../../styles";


const Page = styled.main`
  text-align: left;
  color: #3c3b3b;
`;

const PageTitle = styled.h1`
  color: rgb(0, 55, 103);
`;

const Link = styled.a`
  margin-left: 5px;
  cursor: pointer;
`;

const Loading = styled.div`
  margin: 30px auto;
  font-size: 40px;
  text-align: center;
  color: ${red};
`;

const Intro = styled.p`
  font-size: 17px;
  font-weight: 400;
  line-height: 25px;
  margin-top: 40px;
`;

const StyledButton = styled.button`
  color: white;
  display: inline-block;
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: white;
  text-align: center;
  text-decoration: none;
  vertical-align: middle;
  cursor: pointer;
  -webkit-user-select: none;
  user-select: none;
  border: 1px solid ${red};
  border-radius: 0.375rem;
  background-color: ${red};
  opacity: 0.9;
  margin-bottom: 20px;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:disabled {
    pointer-events: none;
    cursor: not-allowed;
    background-color: #212529;
    border-color: #212529;
  }
  &:not([disabled]):focus,
  &:not([disabled]):hover {
    opacity: 0.8;
  }
`;

const Button = ({ onSubmit, children, loading }) => {
  return (
    <StyledButton className="submit-btn" onClick={onSubmit}>
      {!loading ? (
        children
      ) : (
        <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
      )}
    </StyledButton>
  );
};

Button.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool.isRequired,
};

function SMAdmin() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedButton, setSelectedButton] = useState(null);
 

  const exportFile = (id) => {
    setSelectedButton(id);

    const cacheBuster = Date.now(); // cache busting only
    const url = `/api/smV2ExportEligibleMasterExcel?_cb=${cacheBuster}`;

    const link = document.createElement("a");
    link.href = url;
    link.download = ""; // let server decide filename via Content-Disposition
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => {
      setSelectedButton(null);
    }, 3000);
  };

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const response = await fetch(`/api/isAdmin`, {
          method: "GET",
          mode: "cors",
          cache: "no-cache",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status === 403) {
          setError("You do not have access to this page");
        } else if (response.status === 200) {
          setError(null);
        } else {
          setError("An unexpected error occurred");
        }
      } catch (e) {
        setError("An unexpected error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, []);

  if (isLoading) {
    return (
      <Page>
        <Loading>
          <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
        </Loading>
      </Page>
    );
  } else if (error) {
    return (
      <span className="row">
        <span className="alert alert-danger" role="alert">
          {error}
        </span>
        <br />
      </span>
    );
  }

  return (
    <Page>
      <PageTitle>SOT Apply - Master Report Page for Administrators</PageTitle>

      <Intro>
        This page outlines the steps to populate a cloud database (Azure Cosmos
        DB) and generate a master report listing the Component Group award
        recipients for the selected year. The report is then used by Component
        Group Staff Liaisons to track awards and their corresponding recipients.
      </Intro>

      <p>
        <strong>How to: </strong>
        SOT staff can download the Master Report for the selected award cycle
        year (via the dropdown) from SOT Apply.
      </p>
      <p>
        At the end, a new report must be exported each time updates are
        downloaded (Step #6).
      </p>
      <p>
        <strong>Additional User Documentation:</strong>
      </p>
      <ul>
        <li>
          <Link
            href="https://aimhq.sharepoint.com/:x:/s/SOT/EZh2_lWpd2hIl7WvuqSFrzAB3r9VPsspE2E4zv9-8GMnFA?e=tJq7nD"
            target="_blank"
          >
            Mapping of report fields
          </Link>
        </li>
        <li>
          <Link
            href="https://aimhq.sharepoint.com/:w:/s/SOT/EUxYjX8dhRFFiL0O-SNx6JUBDkR911iJSozbW-LXXxGX8w?e=kuD45E"
            target="_blank"
          >
            Master report SOP
          </Link>
        </li>
      </ul>
      <br />

      <hr style={{ margin: "40px 0px" }}></hr>

      <h3>Download Full Master Report</h3>

      <p>
        {`Clicking the "Export Master Report" button will merge all the
        collections mentioned above and generate a CSV file containing the
        filtered data from Azure. Please note that if an application has no
        completed tasks or reviews in Apply, it will be excluded from the merge
        process and will not appear in the report.`}
      </p>
      <p>
        Additionally, this report will not include updated data unless the Azure
        collections are refreshed by running the buttons above.
      </p>

      <p>
        <strong>Technical Description:</strong> The button below will join the
        Reviews with the Applications and Tasks data using the application ID
        and export the data as a CSV file. The report data is stored in the
        MasterReport collection
      </p>
      <Button onSubmit={() => exportFile(6)} loading={selectedButton === 6}>
        Generate and Export Master Report
      </Button>
      <br />
      <br />
      <br />
      <br />
    </Page>
  );
}

export default SMAdmin;
